import { useEffect, useState } from "react";
import Switch from "../../../components/common/Switch";
import Select from "../../../components/common/Select";
import Button from "../../../components/common/button";
import DataTable from "react-data-table-component";
import { VOLUME_OPTIONS } from "../../../utils/constants";
import AddAlarmMusicModal from "../../../components/tv-app/alarm/add-alarm-music-modal";
import DeleteAndroidHome from "../../../components/tv-app/delete-android-home";
import EditTableRowName from "../../../components/tv-app/edit-name";
import DiscardSaveButtons from "../../../components/tv-app/discard-save-buttons";
import {
  ICategoryComponentProps,
  ISounds,
  ITableColumn,
} from "../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import useUpdateAndroidTVHomeOrder from "../../../hooks/use-update-android-home-order";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../components/common/DragRow";
import { DragSvg } from "../../../icons";
import { setDragOnlyInStaging } from "../../../utils/helpers";
import { useGetUserPermission } from "../../../context/AuthStaffUserContext";

type IFormData = {
  showAlarm: boolean;
  soundsData: ISounds[];
  volume: { label: string; value: string };
};

const Alarm = ({ data, loading, onSave }: ICategoryComponentProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const getInitialFormData = () => {
    return {
      showAlarm: true,
      soundsData: data?.sounds?.sort((a, b) => a?.orderKey - b?.orderKey) || [],
      volume:
        VOLUME_OPTIONS.find(
          (item) => item.value === data?.alarmSettings?.volume
        ) || VOLUME_OPTIONS[0],
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });
  const { reset, handleSubmit } = methods;

  const {
    fields: alarmFields,
    append: appendAlarmField,
    remove: removeAlarmField,
  } = useFieldArray({
    name: "soundsData",
    control: methods.control,
    keyName: "customId",
  });

  const categoryName = data?.categoryName || "";

  const [alarmData, setAlarmData] = useState(() => ({
    categoryName,
    items:
      [...(alarmFields as ISounds[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    // Sort and update alarmData.items whenever alarmFields changes
    const sorted = [...alarmFields].sort((a, b) => a.orderKey - b.orderKey);
    setAlarmData((prev) => ({
      ...prev,
      items: sorted,
    }));
  }, [alarmFields]);

  useEffect(() => {
    // setSoundsData(JSON.parse(JSON.stringify(data?.sounds)));
    if (alarmFields.length < data.sounds.length) {
      const addedElement = data.sounds[data.sounds.length - 1];
      appendAlarmField({ ...addedElement });
      setInitialFormData((pre) => ({
        ...pre,
        soundsData: [...pre.soundsData],
      }));
    }
  }, [data.sounds]);

  const getTableDataIndex = (id: string) => {
    return alarmFields.findIndex((item) => item.id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...alarmData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));
    setAlarmData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));

    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));

    updateAndroidTVHomeOrder({ categoryName, data: updatedDataWithOrderKey });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      alarmData.items.map((item, index) => [
        item.id,
        item.orderKey || index + 1,
      ])
    );

    const mergedData = submitData.soundsData
      .sort((a, b) => a?.orderKey - b?.orderKey)
      .map((item) => ({
        ...item,
        orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
      }));
    onSave({
      categoryName: data?.categoryName,
      data: {
        ...data,
        alarmSettings:
          "show" in (data?.alarmSettings || {})
            ? {
                ...data.alarmSettings,
                volume: submitData.volume.value,
                show: submitData.showAlarm,
              }
            : {
                show: submitData.showAlarm,
                volume: submitData.volume.value,
                subData: data.alarmSetting,
              },
        sounds: mergedData,
      },
    });
  };

  const handleSwitchChange = (rowId: string) => {
    const updatedItems = alarmData.items
      .sort((a, b) => a?.orderKey - b?.orderKey)
      .map((item, index) => {
        const isSelected = item.id === rowId;

        methods.setValue(`soundsData.${index}.isSelected`, isSelected);

        return {
          ...item,
          isSelected,
        };
      });

    methods.trigger();

    setAlarmData({ ...alarmData, items: updatedItems });
  };
  const { write: isEditPermission } = useGetUserPermission("tv-app");

  const tableColumns: ITableColumn<ISounds>[] = setDragOnlyInStaging<
    ITableColumn<ISounds>
  >([
    {
      name: "",
      cell: (Row, index) => (
        <DraggableRow
          index={index}
          moveRow={moveRow}
          onDropComplete={onDropComplete}
        >
          <DragSvg
            height={24}
            width={24}
            className="h-6 w-6 cursor-grab flex-none"
          />
        </DraggableRow>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Name",
      cell: (Row) => (
        <EditTableRowName
          name={`soundsData.${getTableDataIndex(Row.id)}.name`}
          disabled={!isEditPermission}
        />
      ),
      sortable: true,
      width: "40%",
    },
    {
      name: "Default",
      cell: (Row) => (
        <Switch
          name={`soundsData.${getTableDataIndex(Row.id)}.isSelected`}
          onCheck={() => handleSwitchChange(Row.id)}
        />
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Hide/Show",
      cell: (Row) => (
        <Switch name={`soundsData.${getTableDataIndex(Row.id)}.selected`} />
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
    {
      name: "Status",
      cell: (Row) => (
        <DeleteAndroidHome
          categoryName={data?.categoryName}
          deleteId={Row.id}
          disabled={!isEditPermission}
          removeField={() => {
            setInitialFormData((pre) => ({
              ...pre,
              soundsData: pre.soundsData.filter((item) => item.id !== Row.id),
            }));
            removeAlarmField(getTableDataIndex(Row.id));
            setAlarmData({
              ...alarmData,
              items: alarmData.items.filter((ele) => ele.id !== Row.id),
            });
          }}
        />
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
  ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!alarmData?.items) return;

    const globalFromIndex = (currentPage - 1) * pageSize + fromIndex;
    const globalToIndex = (currentPage - 1) * pageSize + toIndex;

    const updatedData = [...alarmData.items];
    const [movedItem] = updatedData.splice(globalFromIndex, 1);
    updatedData.splice(globalToIndex, 0, movedItem);

    const reOrdered = updatedData.map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setAlarmData({
      ...alarmData,
      items: reOrdered,
    });
  };

  return (
    <FormProvider {...methods}>
      <div>
        <h3 className="text-[22px] font-normal text-[#111827]">Alarm</h3>
        <div className="">
          {/* <Switch name="showAlarm" label="Alarm (Hide/Show)" /> */}
        </div>
        <div className="grid grid-cols-3 gap-2 relative">
          <Select
            label="Select Volume"
            options={VOLUME_OPTIONS}
            name="volume"
          />
        </div>
        <div className="flex items-center justify-between w-full mt-7.5">
          <h4 className="text-base leading-[39px] font-normal text-[#111827]">
            Music List
          </h4>
          <Button
            variant="primary"
            className="w-21"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add
          </Button>
        </div>
        {/* <div className="h-full max-h-[calc(100vh-520px)] overflow-auto mt-[17px]"> */}
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={alarmData?.items || []}
            columns={tableColumns}
            // striped={true}
            keyField="customId"
            persistTableHead
            pagination={alarmFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(perPage) => setPageSize(perPage)}
          />
        </DndProvider>
        {/* </div> */}

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData());
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddAlarmMusicModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={data.categoryName}
        />
      </div>
    </FormProvider>
  );
};

export default Alarm;
